import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    fetchScopedAccounts,
    type Organization,
    type OrgLevel,
    type ScopedAccountIssue,
    type ScopedAccountRow,
    type ScopedAccountsParams,
} from '@/api/orgAdmin';
import { filterParams } from './scopedAccountsQuery';
import type { FacetGroup, FacetGroupKey, FacetOption, ScopedAccountsFilters } from './types';

const ALL_ISSUES: ScopedAccountIssue[] = ['no_level', 'stale_role_map'];

/** One sidebar count request. `value` doubles as the option's `FacetOption.key`. */
interface FacetRequest {
    group: FacetGroupKey;
    value: string;
    params: ScopedAccountsParams;
}

export interface ScopedAccountFacetsArgs {
    apiBase: string;
    token: string | null;
    enabled: boolean;
    orgId: string | null;
    orgName: string | null;
    childOrgs: Organization[];
    activeLevels: OrgLevel[];
    filters: ScopedAccountsFilters;
    /**
     * EAA-1839 (2026-08-24, user report: "api accounts call too much"): the
     * SAME rows `useScopedAccounts`'s capped walk already fetches for grouping
     * and search — `null` while that walk has not resolved yet. When it HAS
     * resolved and is not `walkCapped`, this hook derives `level`/`childOrg`/
     * `status` counts (and the `no_level` issue count) straight from these rows
     * instead of firing a `page_size=1` request per value — see `countOf`'s
     * docstring for exactly when that is provably correct, not merely faster.
     */
    walkRows: readonly ScopedAccountRow[] | null;
    /** True when the walk hit its page cap — see `walkRows`'s docstring. A
     *  capped set under-counts, so derivation is skipped and every group falls
     *  back to the server request it always used, same as before this existed. */
    walkCapped: boolean;
}

/**
 * The three frame-35 rail groups that have no server param and no row field to
 * derive from. Kept as data (rather than inlined JSX) so the reason travels with
 * the option and one i18n key covers every row of a group.
 */
const UNBACKED_GROUPS: { key: string; options: string[] }[] = [
    { key: 'source', options: ['console', 'sync'] },
    { key: 'lastLogin', options: ['7d', '30d', 'over90d', 'never'] },
    { key: 'warning', options: ['emailUnverified', 'no2fa', 'duplicateEmail'] },
];

/**
 * EAA-1839: the counted filter facets of frame 34's left column.
 *
 * ONE REQUEST PER OPTION, and that is forced. portal-api exposes no aggregate or
 * facet-count endpoint for this listing — `provisioning-status` is per-org and
 * Assignment-status shaped (synced/pending/error/revoked), not per-Level or
 * per-đơn vị — so the only way to answer "how many rows carry Level X" is to ask
 * for that filter and read `.count`. Each request therefore uses `page_size=1`:
 * the single returned row is discarded and only the total is used. The counts are
 * the reason the sidebar exists in the design, so the alternative was a sidebar of
 * unlabelled toggles.
 *
 * Split out of `useScopedAccounts` for the same reason `useRoleMapView` is split
 * out of `useRoleMapTable`: this is pure derivation over the filter state, and
 * keeping it here leaves that hook as "which rows are on screen and which are
 * ticked".
 *
 * `staleTime` is 30s so that merely paging the table (which does not change any
 * filter) never re-fires the whole fan-out.
 *
 * EAA-1839 (frame 35 completion pass): frame 35's rail carries SIX groups, not
 * four. The three new ones — Nguồn, Đăng nhập, Cảnh báo — are rendered as visible
 * but permanently inert rows, each with the real reason, for exactly the precedent
 * the `never_logged_in` row already set in this file. None of the three has a data
 * source: `OrganizationAccountsView.get` reads `include_children` / `issue` /
 * `level_id` / `child_organization_id` / `is_active` / `ordering` and nothing else,
 * and `AssignmentSerializer` carries no provenance field, no login instant and no
 * email-verification/2FA flag to derive them from client-side either. The
 * alternative considered and rejected was dropping the three groups: the counts a
 * later backend adds belong in these positions, and a rail that silently lacks
 * "Cảnh báo" reads as "there are no warnings to filter by".
 */
export function useScopedAccountFacets(args: ScopedAccountFacetsArgs): FacetGroup[] {
    const { t } = useTranslation();
    const {
        apiBase,
        token,
        enabled,
        orgId,
        orgName,
        childOrgs,
        activeLevels,
        filters,
        walkRows,
        walkCapped,
    } = args;

    /**
     * EAA-1839: whether group G's per-value counts can be computed from
     * `walkRows` instead of a server request — for EVERY value in the group at
     * once, not chosen per value.
     *
     * `filterParams(filters, G)` (the params each of G's server requests already
     * used) is "every currently-active filter EXCEPT G's own". `walkRows` was
     * fetched with `filterParams(filters)` — every filter, G's own included. Those
     * two are the SAME query exactly when G's own filter is currently unset: G
     * contributes nothing to `filterParams(filters)` in that case, so the walk's
     * rows already ARE the universe "every other filter as-is, no constraint on
     * G" that a facet count needs — grouping them by G's raw field is not an
     * approximation, it is the same count the server would return, computed from
     * data already in hand instead of an extra round trip.
     *
     * The moment G's own filter IS set, the walk is narrowed to that one value,
     * and no longer reflects what an UNTICKED option in the same group would
     * count — so derivation is intentionally NOT attempted there, and that
     * group's requests fire exactly as before. Same fallback while `walkRows` is
     * `null` (not resolved yet) or `walkCapped` (an incomplete prefix would
     * under-count).
     */
    const canDerive = Boolean(walkRows) && !walkCapped;
    const levelDerivable = canDerive && filters.levelId === null;
    const statusDerivable = canDerive && filters.isActive === null;
    const childOrgDerivable = canDerive && filters.childOrgId === null;
    // `stale_role_map` needs a RoleMap cross-reference no row field carries, so
    // only the `no_level` VALUE within the `issue` group derives — the group's
    // own filter still has to be unset for the same reason as every other group.
    const noLevelIssueDerivable = canDerive && filters.issue === null;

    /**
     * EAA-1839 (2026-08-24, second user report — measured live, not assumed):
     * a group whose axis is unset is derivable IN PRINCIPLE, but `walkRows` is
     * `null` until the walk's own request resolves. Building that group's server
     * requests on THIS render (reasoning "not derivable YET, so ask the server")
     * and then omitting them once the walk resolves does not un-send them — the
     * fetch already left the browser. Measured in the network panel: `level`'s
     * requests happened to never fire only because `activeLevels` (itself
     * async-loaded) was still empty on the one render before the walk resolved,
     * by luck of load order; `status`/`childOrg`/`no_level`'s axes need no async
     * list, so THEIR first render already had everything needed to build a
     * request, and did — 6 requests still fired that a friendlier race would
     * have skipped.
     *
     * The fix is to WAIT rather than fire-then-discard: while an axis is unset
     * and `walkRows` has not resolved, this hook does not yet know whether it
     * will derive or fall back, so it builds NO request for that group at all
     * (the sidebar shows its existing "not answered yet" `null` state, same as
     * any other loading facet). Only once the walk settles does the group
     * either derive (no request, ever) or — if capped — build the request it
     * always would have. An axis that is currently SET skips this wait
     * entirely: derivation is already impossible regardless of the walk, so
     * there is nothing to gain by delaying that group's request.
     */
    const levelWaiting = filters.levelId === null && !walkRows;
    const statusWaiting = filters.isActive === null && !walkRows;
    const childOrgWaiting = filters.childOrgId === null && !walkRows;
    const noLevelIssueWaiting = filters.issue === null && !walkRows;

    const derivedCounts = useMemo(() => {
        const map = new Map<string, number>();
        if (!walkRows) return map;
        if (levelDerivable) {
            const byLevel = new Map<string, number>();
            for (const row of walkRows) {
                if (!row.level_id) continue;
                byLevel.set(row.level_id, (byLevel.get(row.level_id) ?? 0) + 1);
            }
            for (const level of activeLevels) {
                map.set(`level:${level.id}`, byLevel.get(level.id) ?? 0);
            }
        }
        if (statusDerivable) {
            let active = 0;
            for (const row of walkRows) if (row.is_active) active += 1;
            map.set('status:true', active);
            map.set('status:false', walkRows.length - active);
        }
        if (childOrgDerivable && filters.includeChildren && orgId) {
            const byOrg = new Map<string, number>();
            for (const row of walkRows) {
                byOrg.set(row.organization_id, (byOrg.get(row.organization_id) ?? 0) + 1);
            }
            for (const target of [orgId, ...childOrgs.map((child) => child.id)]) {
                map.set(`childOrg:${target}`, byOrg.get(target) ?? 0);
            }
        }
        if (noLevelIssueDerivable) {
            map.set('issue:no_level', walkRows.filter((row) => !row.level_id).length);
        }
        return map;
    }, [
        walkRows,
        levelDerivable,
        statusDerivable,
        childOrgDerivable,
        noLevelIssueDerivable,
        activeLevels,
        childOrgs,
        filters.includeChildren,
        orgId,
    ]);

    const facetRequests = useMemo<FacetRequest[]>(() => {
        const requests: FacetRequest[] = [];
        for (const issue of ALL_ISSUES) {
            // EAA-1839: `no_level` is the one ISSUE VALUE (not the whole group —
            // `stale_role_map` has no row field to derive from) that skips its
            // request — permanently once derivable, temporarily while waiting to
            // find out (see the docstring above `levelWaiting`).
            if (issue === 'no_level' && (noLevelIssueDerivable || noLevelIssueWaiting)) continue;
            requests.push({
                group: 'issue',
                value: issue,
                params: { ...filterParams(filters, 'issue'), issue, pageSize: 1 },
            });
        }
        // EAA-1839: the level/childOrg/status fan-outs below are skipped ENTIRELY
        // (not per value) once their group is derivable, AND while it is still
        // WAITING to find out — see the docstring above `levelWaiting`. This is
        // where the actual network requests stop firing, not just where the
        // server's answer gets ignored client-side.
        if (!levelDerivable && !levelWaiting) {
            for (const level of activeLevels) {
                requests.push({
                    group: 'level',
                    value: level.id,
                    params: { ...filterParams(filters, 'level'), levelId: level.id, pageSize: 1 },
                });
            }
        }
        // Only fanned out while children are in scope: with `include_children` off
        // the effective scope is the single org, and any child id would make the
        // server return nothing at all (its deliberate fail-closed).
        if (filters.includeChildren && orgId && !childOrgDerivable && !childOrgWaiting) {
            for (const target of [orgId, ...childOrgs.map((child) => child.id)]) {
                requests.push({
                    group: 'childOrg',
                    value: target,
                    params: {
                        ...filterParams(filters, 'childOrg'),
                        childOrganizationId: target,
                        pageSize: 1,
                    },
                });
            }
        }
        if (!statusDerivable && !statusWaiting) {
            for (const isActive of [true, false]) {
                requests.push({
                    group: 'status',
                    value: String(isActive),
                    params: { ...filterParams(filters, 'status'), isActive, pageSize: 1 },
                });
            }
        }
        return requests;
    }, [
        filters,
        activeLevels,
        childOrgs,
        orgId,
        levelDerivable,
        levelWaiting,
        childOrgDerivable,
        childOrgWaiting,
        statusDerivable,
        statusWaiting,
        noLevelIssueDerivable,
        noLevelIssueWaiting,
    ]);

    const facetQueries = useQueries({
        queries: facetRequests.map((request) => ({
            queryKey: [
                'scoped-accounts-facet',
                token,
                orgId,
                request.group,
                request.value,
                request.params,
            ],
            queryFn: () =>
                fetchScopedAccounts(apiBase, token as string, orgId as string, request.params),
            enabled,
            retry: false,
            staleTime: 30_000,
        })),
    });

    const facetCounts = useMemo(() => {
        const map = new Map<string, number | null>();
        facetRequests.forEach((request, index) => {
            const query = facetQueries[index];
            // `null`, never 0, when the request has not answered — a placeholder
            // zero beside "Chưa có Level" would read as "no problems here".
            map.set(`${request.group}:${request.value}`, query?.data?.count ?? null);
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facetRequests, facetQueries.map((q) => q.dataUpdatedAt + ':' + q.status).join('|')]);

    const countOf = useCallback(
        (group: FacetGroupKey, value: string) => {
            const key = `${group}:${value}`;
            // EAA-1839: `derivedCounts` first — it is only ever populated for a
            // (group, value) pair whose SERVER request was skipped (see
            // `facetRequests` above), so the two maps are disjoint by
            // construction; this is not a preference between two live answers,
            // it is reading whichever one exists.
            const derived = derivedCounts.get(key);
            if (derived !== undefined) return derived;
            return facetCounts.get(key) ?? null;
        },
        [derivedCounts, facetCounts]
    );

    return useMemo<FacetGroup[]>(() => {
        // EAA-1845: built per-render off `t` rather than a module-level map, so the
        // facet copy reacts to the active language.
        const issueLabels: Record<ScopedAccountIssue, string> = {
            no_level: t('console.hierarchy.scopedAccounts.facets.issueNoLevel'),
            stale_role_map: t('console.hierarchy.scopedAccounts.facets.issueStaleRoleMap'),
        };
        const issueOptions: FacetOption[] = ALL_ISSUES.map((issue) => ({
            key: issue,
            label: issueLabels[issue],
            count: countOf('issue', issue),
            selected: filters.issue === issue,
            tone: 'warn' as const,
        }));
        // Kept visible but permanently inert — `issue=never_logged_in` is a
        // VALIDATION_ERROR server-side, not an unimplemented no-op.
        issueOptions.push({
            key: 'never_logged_in',
            label: t('console.hierarchy.scopedAccounts.facets.neverLoggedInLabel'),
            count: null,
            selected: false,
            disabledReason: t('console.hierarchy.scopedAccounts.facets.neverLoggedInReason'),
        });

        const levelOptions: FacetOption[] = activeLevels.map((level) => ({
            key: level.id,
            label: level.name,
            count: countOf('level', level.id),
            selected: filters.levelId === level.id,
        }));

        const childOrgOptions: FacetOption[] = [];
        if (filters.includeChildren && orgId) {
            childOrgOptions.push({
                key: orgId,
                label: t('console.hierarchy.scopedAccounts.facets.directOrgLabel', {
                    org: orgName ?? t('console.hierarchy.scopedAccounts.facets.orgFallback'),
                }),
                count: countOf('childOrg', orgId),
                selected: filters.childOrgId === orgId,
            });
            for (const child of childOrgs) {
                childOrgOptions.push({
                    key: child.id,
                    label: child.name,
                    count: countOf('childOrg', child.id),
                    selected: filters.childOrgId === child.id,
                });
            }
        }

        const statusOptions: FacetOption[] = [
            {
                key: 'true',
                label: t('console.hierarchy.scopedAccounts.facets.statusActiveLabel'),
                count: countOf('status', 'true'),
                selected: filters.isActive === true,
            },
            {
                key: 'false',
                label: t('console.hierarchy.scopedAccounts.facets.statusInactiveLabel'),
                count: countOf('status', 'false'),
                selected: filters.isActive === false,
            },
        ];

        const unbackedGroups: FacetGroup[] = UNBACKED_GROUPS.map((group) => ({
            key: group.key,
            label: t(`console.hierarchy.scopedAccounts.facets.group_${group.key}`),
            options: group.options.map((option) => ({
                key: option,
                label: t(`console.hierarchy.scopedAccounts.facets.${group.key}_${option}`),
                count: null,
                selected: false,
                disabledReason: t(
                    `console.hierarchy.scopedAccounts.facets.${group.key}Unavailable`
                ),
            })),
            note: t(`console.hierarchy.scopedAccounts.facets.${group.key}Unavailable`),
        }));

        return [
            {
                key: 'issue',
                label: t('console.hierarchy.scopedAccounts.facets.groupIssue'),
                options: issueOptions,
            },
            {
                key: 'level',
                label: t('console.hierarchy.scopedAccounts.facets.groupLevel'),
                options: levelOptions,
                note: filters.includeChildren
                    ? t('console.hierarchy.scopedAccounts.facets.levelScopeNote')
                    : undefined,
            },
            {
                key: 'childOrg',
                label: t('console.hierarchy.scopedAccounts.facets.groupOrg'),
                options: childOrgOptions,
                note: filters.includeChildren
                    ? t('console.hierarchy.scopedAccounts.facets.childOrgNote')
                    : t('console.hierarchy.scopedAccounts.facets.childOrgOffNote'),
            },
            {
                key: 'status',
                label: t('console.hierarchy.scopedAccounts.facets.groupStatus'),
                options: statusOptions,
            },
            ...unbackedGroups,
        ];
    }, [filters, activeLevels, childOrgs, orgName, orgId, countOf, t]);
}
